import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-daisyui';

const TermsPage: FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      window.close();
    }
  };

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto">
      <div className="flex flex-col gap-6 w-full mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Logo */}
        <div className="flex justify-center">
          <img src="/assets/logo.png" alt="CareTailor" className="h-10 w-auto" />
        </div>
      {/* Header */}
      <div className="relative flex items-center justify-center">
        <Button
          color="ghost"
          shape="circle"
          className="absolute left-0 w-12 h-12 bg-black hover:bg-black text-white shadow-sm grid place-items-center rounded-xl"
          onClick={handleBack}
        >
          <img src="/assets/arrowLeft.svg" alt="Back" style={{ width: '16.5px', height: '15px' }} />
        </Button>
        <h1 className="text-lg font-semibold text-black">Terms &amp; Conditions</h1>
      </div>

      {/* Content */}
      <div className="bg-[#F6F6F4] rounded-[20px] shadow-sm p-4 md:p-6 lg:p-8 leading-relaxed text-sm md:text-base text-gray-800 space-y-6">
        <div className="space-y-1">
          <p className="font-semibold text-black">CareTailor Ltd</p>
          <p className="text-gray-500 text-sm">Last updated: 29/03/2026</p>
        </div>

        <p>
          These terms govern your use of the CareTailor platform, provided by CareTailor Ltd, a company registered in
          England and Wales (company number 16534918, registered office 20 Wenlock Road, London, England, N1 7GU).
        </p>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">1. What CareTailor does</h2>
          <p>CareTailor Ltd helps people find and connect with independent carers.</p>
          <p className="font-medium">We:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Introduce carers and clients</li>
            <li>Provide tools to communicate</li>
          </ul>
          <p className="font-medium">We do not:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Provide care</li>
            <li>Employ carers</li>
            <li>Manage care arrangements</li>
            <li>Act as a care agency</li>
          </ul>
          <p>Any care arrangement is entirely between the client and the carer.</p>
          <p>
            CareTailor Ltd acts as an introductory platform only and does not endorse or recommend any individual carer.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">2. Your agreement</h2>
          <p>By using the CareTailor platform, you agree to these terms.</p>
          <p>You must be 18+ and provide accurate information.</p>
          <p>CareTailor Ltd may suspend or remove accounts at any time.</p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">3. Choosing and providing care</h2>
          <p className="font-medium">Clients:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>You are responsible for choosing a suitable carer.</li>
            <li>You must check identity, experience, qualifications, and references.</li>
            <li>You must be comfortable before agreeing to any care.</li>
            <li>
              All care agreements, including schedules, rates, and cancellations, must be agreed directly with the
              carer. We recommend putting agreements in writing.
            </li>
          </ul>
          <p className="font-medium">Carers:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>You are responsible for the services you provide, delivering care safely and appropriately.</li>
            <li>You must set your own rates, availability, and cancellation terms with clients.</li>
            <li>Only accept work you are competent to provide safely.</li>
            <li>All agreements must be confirmed in writing.</li>
          </ul>
          <p>All agreements are direct between client and carer. CareTailor Ltd is not part of any agreement.</p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">4. Checks and safety</h2>
          <p>CareTailor Ltd carries out limited checks on carers before they join the platform:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Enhanced DBS (Disclosure and Barring Service) checks</li>
            <li>Proof of insurance</li>
          </ul>
          <p>
            CareTailor Ltd does not verify qualifications, experience, or references beyond what carers provide.
          </p>
          <p>We rely on information provided by carers and do not independently verify all information.</p>
          <p className="font-medium">We do not guarantee:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Accuracy of information provided by carers</li>
            <li>A carer's suitability, competence, or experience</li>
            <li>The quality or safety of care</li>
          </ul>
          <p>Clients are responsible for carrying out their own checks before engaging a carer.</p>
          <p>
            CareTailor Ltd reserves the right to remove any user and report concerns to relevant authorities where we
            believe there may be a risk to safety or wellbeing.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">5. Insurance</h2>
          <p>CareTailor Ltd does not provide insurance.</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Carers must maintain their own insurance.</li>
            <li>
              Clients should ensure they are comfortable with arrangements and may review their home insurance coverage.
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">6. Acceptable use</h2>
          <p className="font-medium">You must:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Act lawfully and respectfully</li>
            <li>Treat others with dignity and respect</li>
          </ul>
          <p className="font-medium">You must not:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Copy or reuse platform content</li>
            <li>Collect or scrape data</li>
            <li>Misuse the platform</li>
          </ul>
          <p>CareTailor Ltd may remove users who breach these rules.</p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">7. Disputes</h2>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Any issues must be resolved directly between the client and the carer.</li>
            <li>CareTailor Ltd does not mediate disputes and is not responsible for outcomes of care.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">8. Liability</h2>
          <p>To the fullest extent permitted by law, CareTailor Ltd is not liable for:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Actions or omissions of carers or clients</li>
            <li>Quality or outcome of care</li>
            <li>Any loss or damage arising from use of the platform</li>
          </ul>
          <p>
            Nothing excludes liability where it cannot legally be limited (including death or personal injury caused by
            negligence).
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">9. Changes</h2>
          <p>CareTailor Ltd may update these terms at any time.</p>
          <p>Continued use means you accept the updated terms.</p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">10. Legal</h2>
          <p>These terms are governed by English law.</p>
        </section>

        <section className="space-y-2">
          <h2 className="font-semibold text-black">11. Contact</h2>
          <a href="mailto:support@caretailor.co.uk" className="text-primary underline">
            support@caretailor.co.uk
          </a>
        </section>

        <div className="border-t border-gray-200 pt-6 space-y-2">
          <h2 className="font-semibold text-black">Carer Terms (Additional)</h2>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>You are self-employed and not employed by CareTailor Ltd.</li>
            <li>
              You are fully responsible for the care you provide, your conduct, and compliance with laws.
            </li>
          </ul>
          <p className="font-medium">You confirm that:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Information you provide is accurate</li>
            <li>You have the right to work in the UK</li>
            <li>You hold valid insurance</li>
            <li>Your DBS is up to date</li>
            <li>You will only accept work you can provide safely</li>
          </ul>
          <p>CareTailor Ltd does not supervise, direct, or control how you provide care.</p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TermsPage;
